import { useState, useEffect } from 'react';
import { z } from 'zod';

export function useStorage(
  store: Storage,
  key: string | null,
): ReturnType<typeof useState<unknown>>;

export function useStorage<T extends z.ZodTypeAny>(
  store: Storage,
  key: string | null,
  schema: T,
): ReturnType<typeof useState<z.infer<T> | null>>;

export function useStorage<T extends z.ZodTypeAny | undefined>(
  store: Storage,
  key: string | null,
  schema?: T,
): ReturnType<typeof useState<null | MaybeZod<T>>> {
  const [value, setValue] = useState(() =>
    tryGetFromStorage(key, schema, store),
  );

  useEffect(() => {
    setValue(tryGetFromStorage(key, schema, store));
  }, [key, schema]);

  useEffect(() => {
    if (key) {
      trySetToStorage(key, value, store);
    }
  }, [key, value]);

  return [value, setValue] satisfies ReturnType<
    typeof useState<null | MaybeZod<T>>
  >;
}

/** Infers the zod type or returns undefined if Z is not a ZodType */
type MaybeZod<Z> = Z extends z.ZodTypeAny ? z.infer<Z> : undefined;

const tryGetFromStorage = <Z extends z.ZodTypeAny | undefined>(
  key: string | null,
  schema: Z,
  store: Storage,
): null | MaybeZod<Z> => {
  if (!key) {
    return null;
  }
  const str = store.getItem(key);
  if (!str || str === 'null') {
    return null;
  }
  try {
    const item = JSON.parse(str);
    return schema ? schema.parse(item) : item;
  } catch (e) {
    console.error(`Error parsing JSON from storage for key "${key}":`, e);
    return null;
  }
};

const trySetToStorage = (key: string, value: unknown, store: Storage) => {
  try {
    store.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(`Error setting value to storage for key "${key}":`, e);
  }
};
